import React, { useState, useEffect } from "react";
import { Card, Button, Table, Space, Modal, message, Badge, Dropdown, Tag, Tooltip, Drawer, Typography, Spin } from "antd";
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined, MoreOutlined, SettingOutlined, TeamOutlined } from "@ant-design/icons";
import { useLoadList, useDelete } from "../../../hooks";
import { masterDataService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import EmailGroupFilter from "./components/EmailGroupFilter";
import GroupManagementDrawer from "./components/GroupManagementDrawer";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";

const { confirm } = Modal;
const { Text } = Typography;

const EmailGroupList = () => {
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
    });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [drawerMode, setDrawerMode] = useState("view"); // 'view', 'create', 'edit'

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("emailGroups", masterDataService.emailGroup.getEmailGroupList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 删除分组的Hook
    const deleteMutation = useDelete("emailGroups", masterDataService.emailGroup.deleteEmailGroup, {
        onSuccess: () => {
            message.success("删除成功");
            refetch();
            handleDrawerClose(); // 删除成功后关闭抽屉
        },
    });

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <Text type="secondary">正在加载用户信息...</Text>
                    </div>
                </Card>
            </div>
        );
    }

    // 权限检查
    if (!hasPermission("email_group:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <Text type="secondary">您没有查看邮箱分组的权限，请联系管理员。</Text>
                        <Text type="secondary" style={{ display: "block", marginTop: 16, fontSize: 12 }}>
                            当前用户: {user?.username || "未知"} | 权限列表: {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </Text>
                    </div>
                </Card>
            </div>
        );
    }

    // 处理行点击事件
    const handleRowClick = (record) => {
        handleManageAddresses(record);
    };

    // 查看/管理邮箱地址
    const handleManageAddresses = (record) => {
        setSelectedGroup(record);
        setDrawerMode("view");
        setDrawerVisible(true);
    };

    // 编辑分组
    const handleEdit = (record) => {
        if (!hasPermission("email_group:update")) {
            message.error("您没有编辑邮箱分组的权限");
            return;
        }
        setSelectedGroup(record);
        setDrawerMode("edit");
        setDrawerVisible(true);
    };

    // 快速创建分组并添加邮箱
    const handleQuickCreate = () => {
        if (!hasPermission("email_group:create")) {
            message.error("您没有创建邮箱分组的权限");
            return;
        }
        setSelectedGroup(null);
        setDrawerMode("create");
        setDrawerVisible(true);
    };

    // 表格列定义
    const columns = [
        {
            title: "序号",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "分组信息",
            dataIndex: "name",
            key: "name",
            ellipsis: true,
            width: 280,
            render: (text, record) => (
                <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: "auto", fontWeight: 500 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleManageAddresses(record);
                    }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
            width: 100,
            render: (text) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {text}
                </Text>
            ),
        },
        {
            title: "标签",
            dataIndex: "tags",
            key: "tags",
            width: 100,
            render: (text, record) => {
                if (!record.tags || record.tags.length === 0) return null;

                return (
                    <Space wrap size={[4, 4]}>
                        {record.tags.slice(0, 3).map((tag, index) => (
                            <Tag key={index} size="small" color="blue">
                                {tag}
                            </Tag>
                        ))}
                        {record.tags.length > 3 && (
                            <Tooltip title={record.tags.slice(3).join(", ")}>
                                <Tag size="small" color="default">
                                    +{record.tags.length - 3}
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "邮箱统计",
            dataIndex: "address_count",
            key: "address_count",
            width: 100,
            align: "center",
            render: (count) => (
                <Text strong style={{ color: count > 0 ? "#1890ff" : "#d9d9d9" }}>
                    {count || 0}
                </Text>
            ),
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (text) => <Badge status={text ? "success" : "error"} text={text ? "启用" : "停用"} />,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 160,
            render: (text) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatDateTime(text)}
                </Text>
            ),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="email_group:update">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(record);
                            }}
                        >
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="email_group:delete">
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "edit",
                                        label: "删除分组",
                                        icon: <DeleteOutlined />,
                                        danger: true,
                                        onClick: () => handleDelete(record),
                                        disabled: !record.is_active,
                                    },
                                ],
                            }}
                            placement="bottomRight"
                            trigger={["click"]}
                        >
                            <Button type="link" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                        </Dropdown>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 处理表格分页变化
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
    };

    // 处理搜索
    const handleSearch = (values) => {
        const params = {};

        if (values.search) {
            params.search = values.search;
        }

        if (values.tags) {
            // 确保标签参数是逗号分隔的字符串
            if (Array.isArray(values.tags)) {
                params.tags = values.tags.join(",");
            } else {
                params.tags = values.tags;
            }
        }

        if (values.is_active !== undefined) {
            params.is_active = values.is_active;
        }

        setFilterParams(params);
        setPagination({
            ...pagination,
            current: 1, // 重置到第一页
        });
    };

    // 重置搜索
    const handleReset = () => {
        setFilterParams({});
        setPagination({
            ...pagination,
            current: 1,
        });
    };

    // 删除分组
    const handleDelete = (record) => {
        if (!hasPermission("email_group:delete")) {
            message.error("您没有删除邮箱分组的权限");
            return;
        }

        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: (
                <Space direction="vertical">
                    <Text>确定要删除分组 "{record.name}" 吗？</Text>
                    <Text type="warning" style={{ fontSize: 12 }}>
                        删除分组不会删除其中的邮箱地址，邮箱地址将变为未分组状态。
                    </Text>
                </Space>
            ),
            okText: "确认删除",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                // 修正：将 id 和 params 作为兄弟属性传递给 mutate 函数
                deleteMutation.mutate({ id: record.id, params: { force: true } });
            },
        });
    };

    // 抽屉成功回调
    const handleDrawerSuccess = (result) => {
        // 如果是创建模式，更新selectedGroup以便后续操作
        if (drawerMode === "create" && result?.data) {
            setSelectedGroup(result.data);
            setDrawerMode("edit"); // 切换到编辑模式
        }
        refetch(); // 刷新分组列表以更新邮箱数量
    };

    // 关闭抽屉
    const handleDrawerClose = () => {
        setDrawerVisible(false);
        setSelectedGroup(null);
        setDrawerMode("view");
    };

    // 获取抽屉标题
    const getDrawerTitle = () => {
        switch (drawerMode) {
            case "create":
                return (
                    <Space>
                        <SettingOutlined />
                        <span>创建分组并配置邮箱</span>
                    </Space>
                );
            case "edit":
                return (
                    <Space>
                        <EditOutlined />
                        <span>编辑分组"{selectedGroup?.name}"</span>
                    </Space>
                );
            case "view":
            default:
                return (
                    <Space>
                        <TeamOutlined />
                        <span>管理分组"{selectedGroup?.name}"</span>
                    </Space>
                );
        }
    };

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">邮箱地址与分组</div>
                <PermissionGuard permission="email_group:create">
                    <Button type="primary" icon={<SettingOutlined />} onClick={handleQuickCreate}>
                        创建分组并配置
                    </Button>
                </PermissionGuard>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <EmailGroupFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 分组列表 */}
            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1000 }}
                    onRow={(record) => ({
                        onClick: () => handleRowClick(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </Card>

            {/* 分组管理抽屉 */}
            <Drawer
                title={getDrawerTitle()}
                width="60%"
                open={drawerVisible}
                onClose={handleDrawerClose}
                destroyOnHidden
                styles={{
                    body: { padding: 0 },
                }}
            >
                <GroupManagementDrawer mode={drawerMode} group={selectedGroup} onSuccess={handleDrawerSuccess} onCancel={handleDrawerClose} />
            </Drawer>
        </div>
    );
};

export default EmailGroupList;
